import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import ProfileForm from "@/components/ProfileForm";
import ChartsView from "@/components/ChartsView";
import AllVargasView from "@/components/AllVargasView";
import StrengthsView from "@/components/StrengthsView";
import DashaTimeline from "@/components/DashaTimeline";
import DayPrediction from "@/components/DayPrediction";
import HomeopathyPanel from "@/components/HomeopathyPanel";
import KentianView from "@/components/KentianView";
import MiasmPanel from "@/components/MiasmPanel";
import KingdomsPanel from "@/components/KingdomsPanel";
import ScripturePanel from "@/components/ScripturePanel";
import NakshatraWisdomPanel from "@/components/NakshatraWisdomPanel";
import AtmaDarshanPanel from "@/components/AtmaDarshanPanel";
import ActiveTabExporter from "@/components/ActiveTabExporter";
import ConsultWidget from "@/components/ConsultWidget";
import LifeSpanPanel from "@/components/LifeSpanPanel";
import RedAlertsPanel from "@/components/RedAlertsPanel";
import PanchangPanel from "@/components/PanchangPanel";
import AttributesPanel from "@/components/AttributesPanel";
import NumerologyPanel from "@/components/NumerologyPanel";
import YogasPanel from "@/components/YogasPanel";
import YogaAtlasPanel from "@/components/YogaAtlasPanel";
import NavatharakaPanel from "@/components/NavatharakaPanel";
import ChartViewsPanel from "@/components/ChartViewsPanel";
import VimshottariCsvPanel from "@/components/VimshottariCsvPanel";
import AstroTopicPanel, {
  TURNING_POINT_SUBS, MUHURTA_SUBS, DASHA_TRANSIT_SUBS, REMEDY_SUBS, GOOD_ALERT_SUBS,
} from "@/components/AstroTopicPanel";
import FullLifePanel from "@/components/FullLifePanel";
import DateRangeScanner from "@/components/DateRangeScanner";
import ClientsPanel from "@/components/ClientsPanel";
import MonthlyReportPanel from "@/components/MonthlyReportPanel";
import MasterpieceScreener from "@/components/MasterpieceScreener";
import PinGate, { usePinUnlocked, unlock as unlockPin } from "@/components/PinGate";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useLang, useT } from "@/lib/i18n";
import { AUTH, PROFILE, DASH } from "@/constants/testIds";
import { Trash2, Plus, Download, Sparkles, X, TrendingUp, Film, PenTool, Hand, Smile, BookOpen, Lock, Phone, Camera, Zap, Loader2, LayoutGrid, Map, User } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

export default function Dashboard() {
  const navigate = useNavigate();
  const { lang } = useLang();
  const t = useT();
  // Public mode — no auth. Stub user.
  const user = { user_id: "public", name: "Guest", picture: "" };
  const [profiles, setProfiles] = useState([]);
  const [active, setActive] = useState(null);
  const [chart, setChart] = useState(null);
  const [strengths, setStrengths] = useState(null);
  const [dasha, setDasha] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [pdfDate, setPdfDate] = useState("");
  const [showBanner, setShowBanner] = useState(() => localStorage.getItem("welcome-banner-dismissed") !== "1");
  // Cream (default) / Night theme — persisted in localStorage
  const [theme, setTheme] = useState(() => localStorage.getItem("teja-theme") || "cream");
  useEffect(() => {
    const root = document.documentElement;
    if (theme === "night") root.setAttribute("data-teja-theme", "night");
    else root.removeAttribute("data-teja-theme");
    localStorage.setItem("teja-theme", theme);
  }, [theme]);

  const dismissBanner = () => {
    setShowBanner(false);
    localStorage.setItem("welcome-banner-dismissed", "1");
  };

  const selectProfile = useCallback(async (p) => {
    setActive(p); setChart(null); setStrengths(null); setDasha(null);
    try {
      const [c, s, d] = await Promise.all([api.chart(p.profile_id), api.strengths(p.profile_id), api.dasha(p.profile_id)]);
      setChart(c); setStrengths(s); setDasha(d.dasha);
    } catch (err) {
      console.error("selectProfile failed:", err);
      toast.error("Failed to load chart");
    }
  }, []);
  const loadProfiles = useCallback(async () => {
    const p = await api.listProfiles();
    setProfiles(p);
    if (p.length) {
      setActive((current) => {
        if (!current) selectProfile(p[0]);
        return current;
      });
    } else {
      setShowForm(true);
    }
  }, [selectProfile]);
  const handleCreated = async (p) => {
    setShowForm(false);
    await loadProfiles();
    selectProfile(p);
  };
  const handleDelete = async (p) => {
    if (!window.confirm(`Delete ${p.name}'s chart?`)) return;
    await api.deleteProfile(p.profile_id);
    if (active?.profile_id === p.profile_id) { setActive(null); setChart(null); setStrengths(null); setDasha(null); }
    loadProfiles();
  };
  const handlePdf = () => {
    if (!active) return;
    window.open(api.pdfUrl(active.profile_id, pdfDate || null), "_blank");
  };

  useEffect(() => { loadProfiles(); }, [loadProfiles]);

  // ---- Batch job polling (runs while a batch generation is in flight) ----
  const pollBatchStatus = useCallback(async () => {
    if (!active?.profile_id) return;
    try {
      const lang = localStorage.getItem("jyotish-lang") || "en";
      const data = await api.batchStatus(active.profile_id, lang);
      setBatchJob(data);
      if (data.status === "running") {
        setTimeout(pollBatchStatus, 4000);
      }
    } catch {}
  }, [active?.profile_id]);

  useEffect(() => {
    if (active?.profile_id) pollBatchStatus();
  }, [active?.profile_id, pollBatchStatus]);

  const startBatch = async (force = false) => {
    if (!active?.profile_id) return;
    if (force && !window.confirm(
      "Regenerate all 143 sub-tabs from scratch? This will WIPE the existing readings for this language and re-charge credits for every slug."
    )) return;
    try {
      const lang = localStorage.getItem("jyotish-lang") || "en";
      await api.batchStart(active.profile_id, lang, force);
      setBatchJob({ status: "running", done: 0, total: 143, current: null });
      toast.success(force
        ? "Regenerating all 143 from scratch — 20-40 minutes"
        : "Batch generation started — 20-40 minutes");
      setTimeout(pollBatchStatus, 500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Batch start failed");
    }
  };

  const resumeBatch = async () => {
    if (!active?.profile_id) return;
    try {
      const lang = localStorage.getItem("jyotish-lang") || "en";
      await api.batchResume(active.profile_id, lang);
      setBatchJob((j) => ({ ...j, status: "running", error: null }));
      toast.success("Batch resumed");
      setTimeout(pollBatchStatus, 500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resume failed");
    }
  };

  const cancelBatch = async () => {
    if (!active?.profile_id) return;
    if (!window.confirm("Cancel the running batch? Sub-tabs already generated will be kept.")) return;
    try {
      const lang = localStorage.getItem("jyotish-lang") || "en";
      await api.batchCancel(active.profile_id, lang);
      setBatchJob((j) => ({ ...j, status: "cancelled" }));
      toast.success("Batch cancelled");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    }
  };

  // ---- Full Masterpiece batch (all 5 tab groups at once) ----
  const pollMaster = useCallback(async () => {
    if (!active?.profile_id) return;
    try {
      const lang = localStorage.getItem("jyotish-lang") || "en";
      const data = await api.masterpieceStatus(active.profile_id, lang);
      setMasterJob(data);
      if (data.status === "running") setTimeout(pollMaster, 4000);
    } catch {}
  }, [active?.profile_id]);

  useEffect(() => {
    if (active?.profile_id) pollMaster();
  }, [active?.profile_id, pollMaster]);

  const startMasterpiece = async () => {
    if (!active?.profile_id) return;
    if (!window.confirm(
      "Generate the FULL Masterpiece? This queues every tab — chart-views, Alan Leo phases, 99 BPHS chapters, Atma Darshan, Homeopathy, and all 143 Full-Life sub-tabs — approximately 268 readings. Estimated time: 60-120 minutes."
    )) return;
    try {
      const lang = localStorage.getItem("jyotish-lang") || "en";
      await api.masterpieceStart(active.profile_id, lang);
      setMasterJob({ status: "running", done: 0, total: 268, current: null });
      toast.success("Full Masterpiece generation started — ~60-120 min");
      setTimeout(pollMaster, 500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Masterpiece start failed");
    }
  };

  const cancelMasterpiece = async () => {
    if (!active?.profile_id) return;
    if (!window.confirm("Cancel the Masterpiece batch? Readings already generated will be kept.")) return;
    try {
      const lang = localStorage.getItem("jyotish-lang") || "en";
      await api.masterpieceCancel(active.profile_id, lang);
      setMasterJob((j) => ({ ...j, status: "cancelled" }));
      toast.success("Masterpiece cancelled");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    }
  };

  const resumeMasterpiece = async () => {
    if (!active?.profile_id) return;
    try {
      const lang = localStorage.getItem("jyotish-lang") || "en";
      await api.masterpieceResume(active.profile_id, lang);
      setMasterJob((j) => ({ ...j, status: "running", error: null }));
      toast.success("Masterpiece resumed");
      setTimeout(pollMaster, 500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resume failed");
    }
  };

  // ---- Strategic Life Map (30-page flagship) ----
  const pollLifeMap = useCallback(async () => {
    if (!active?.profile_id) return;
    try {
      const lang = localStorage.getItem("jyotish-lang") || "en";
      const data = await api.strategicLifeMapStatus(active.profile_id, lang);
      setLifeMapJob(data);
      if (data.status === "running") setTimeout(pollLifeMap, 3000);
    } catch {}
  }, [active?.profile_id]);

  useEffect(() => {
    if (active?.profile_id) pollLifeMap();
  }, [active?.profile_id, pollLifeMap]);

  const startLifeMap = async () => {
    if (!active?.profile_id) return;
    if (lifeMapJob.cached && !window.confirm(
      "Regenerate the Strategic Life Map from scratch? A cached version already exists — you can download it for free without regenerating."
    )) return;
    if (!lifeMapJob.cached && !window.confirm(
      "Generate the 30-page Strategic Life Map? This runs 9 Gemini 2.5 Flash syntheses (one per decade) using every cached reading and takes 5–10 minutes."
    )) return;
    try {
      const lang = localStorage.getItem("jyotish-lang") || "en";
      await api.strategicLifeMapStart(active.profile_id, lang);
      setLifeMapJob({ status: "running", done: 0, total: 10, current: null, cached: false });
      toast.success("Strategic Life Map generation started — ~5–10 min");
      setTimeout(pollLifeMap, 500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Life Map start failed");
    }
  };

  const cancelLifeMap = async () => {
    if (!active?.profile_id) return;
    if (!window.confirm("Cancel the Strategic Life Map generation?")) return;
    try {
      const lang = localStorage.getItem("jyotish-lang") || "en";
      await api.strategicLifeMapCancel(active.profile_id, lang);
      setLifeMapJob((j) => ({ ...j, status: "cancelled" }));
      toast.success("Life Map cancelled");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    }
  };

  const downloadLifeMap = () => {
    if (!active?.profile_id) return;
    const lang = localStorage.getItem("jyotish-lang") || "en";
    window.open(api.strategicLifeMapPdfUrl(active.profile_id, lang), "_blank");
  };

  const uploadPhoto = async (file) => {
    if (!active?.profile_id || !file) return;
    try {
      await api.uploadProfilePhoto(active.profile_id, file);
      setPhotoBump((n) => n + 1);
      toast.success("Portrait uploaded — will appear on the Master Blueprint cover");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Photo upload failed");
    }
  };

  const pinOk = true;  // Direct-Access: PIN gate removed — every tab is
                            // open the moment a birth profile is loaded.
  const setPinOk = () => {};

  const FREE_TABS = new Set(["charts", "chartviews"]);
  const [tabValue, setTabValue] = useState("charts");
  const [pinPrompt, setPinPrompt] = useState(false);
  const [pendingTab, setPendingTab] = useState(null);
  // Batch generation of all 143 sub-tabs
  const [batchJob, setBatchJob] = useState({ status: "idle", done: 0, total: 0, current: null });
  const [masterJob, setMasterJob] = useState({ status: "idle", done: 0, total: 0, current: null });
  const [lifeMapJob, setLifeMapJob] = useState({ status: "idle", done: 0, total: 10, current: null, cached: false });
  // Photo upload trigger
  const [photoBump, setPhotoBump] = useState(0);
  const [pinInput, setPinInput] = useState("");
  const [pinErr, setPinErr] = useState("");
  const [showScreener, setShowScreener] = useState(false);

  const handleTabChange = (v) => {
    if (!pinOk && !FREE_TABS.has(v)) {
      setPendingTab(v);
      setPinPrompt(true);
      return; // don't switch until unlocked
    }
    setTabValue(v);
  };

  return (
    <div className="min-h-screen bg-[#0F1115] text-[#E6E1D6]">
      <div className="grain-overlay pointer-events-none fixed inset-0 z-0" />
      {/* -------- Golden brand banner -------- */}
      <div className="relative z-20 bg-gradient-to-r from-[#8B5A00] via-[#E5C158] to-[#8B5A00] border-b border-[#B08A3E]/70 shadow-[0_2px_20px_rgba(212,175,55,0.35)]"
           data-testid="teja-golden-banner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <div className="w-40" />
          <div className="flex items-center gap-4">
            <span className="text-[#3A2A0F] font-heading text-2xl tracking-widest">॥</span>
            <div className="text-center">
              <div className="font-heading text-2xl sm:text-3xl tracking-[0.35em] uppercase text-[#3A2A0F]"
                      style={{ textShadow: "0 1px 0 #FFF9E8, 0 2px 6px rgba(0,0,0,0.15)" }}>
                Teja Astrology Software
              </div>
              <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.4em] font-mono text-[#3A2A0F]/70">
                Dr. Tejakumar Galla · Vedic + Homeopathy Studio
              </div>
            </div>
            <span className="text-[#3A2A0F] font-heading text-2xl tracking-widest">॥</span>
          </div>
          {/* Cream / Night theme toggle */}
          <div className="w-40 flex justify-end">
            <button
              data-testid="theme-toggle-btn"
              onClick={() => setTheme((t) => t === "cream" ? "night" : "cream")}
              title={theme === "cream" ? "Switch to Night mode (evening consultations)" : "Switch to Cream mode"}
              className="flex items-center gap-1.5 border border-[#3A2A0F]/40 bg-[#3A2A0F]/15 hover:bg-[#3A2A0F]/30 text-[#3A2A0F] font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full transition-colors">
              <span className={`inline-block w-2 h-2 rounded-full ${theme === "cream" ? "bg-[#FFF9E8]" : "bg-[#0F1115]"}`} />
              {theme === "cream" ? "Cream" : "Night"}
            </button>
          </div>
        </div>
      </div>

      {/* -------- Horizontal Tools row with per-tool estimated prices -------- */}
      <header className="relative z-10 border-b border-[#B08A3E]/40 bg-[#FBF3DC] sticky top-0 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            {[
              ["/market",              "Stock Market",     TrendingUp, "#4A8FA1", 0],
              ["/vedas",               "Vedas Hall",       BookOpen,   "#B08A3E", 25],
              ["#clients",             "Clients",          User,       "#4A6BA6", 0],
              ["/case-taking",         "Case Taking",      PenTool,    "#7B906F", 15],
              ["/handwriting",         "Handwriting",      PenTool,    "#7A5AA6", 18],
              ["/palmistry",           "Palmistry",        Hand,       "#A85B2E", 18],
              ["/face-reading",        "Face Reading",     Smile,      "#98692B", 18],
            ].map(([to, label, Icon, color, cost]) => {
              const isInternal = to.startsWith("#");
              const commonCls = "flex flex-col items-center gap-0.5 text-[10px] uppercase tracking-widest font-mono border px-2 py-1.5 rounded-md hover:brightness-110 cursor-pointer";
              if (isInternal) {
                return (
                  <button key={to} type="button"
                          data-testid={`header-${label.toLowerCase().replace(/\s+/g,'-')}-link`}
                          onClick={() => setTabValue("clients")}
                          style={{ background: `${color}18`, borderColor: `${color}55`, color }}
                          className={commonCls}>
                    <span className="flex items-center gap-1">
                      <Icon size={11} /> {t ? t(label) : label}
                    </span>
                    <span className="text-[8px] opacity-80 font-mono">
                      {cost === 0 ? "FREE" : `≈ ₹${cost}`}
                    </span>
                  </button>
                );
              }
              return (
                <Link key={to} to={to}
                       data-testid={`header-${label.toLowerCase().replace(/\s+/g,'-')}-link`}
                       style={{ background: `${color}18`, borderColor: `${color}55`, color }}
                       className={commonCls}>
                  <span className="flex items-center gap-1">
                    <Icon size={11} /> {t ? t(label) : label}
                  </span>
                  <span className="text-[8px] opacity-80 font-mono">
                    {cost === 0 ? "FREE" : `≈ ₹${cost}`}
                  </span>
                </Link>
              );
            })}
          </div>
          <div className="flex items-center gap-2">
            <a href="tel:+916300592183" data-testid="header-phone"
               className="hidden sm:flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-mono
                          text-[#3A2A0F] border border-[#B08A3E]/60 hover:bg-[#B08A3E] hover:text-white
                          px-3 py-2 rounded-sm transition-colors">
              <Phone size={12} /> +91 63005 92183
            </a>
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      {/* Welcome banner — always visible (no PIN required) */}
      {showBanner && (
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 pt-4 sm:pt-6">
          <div className="relative border border-[#D4AF37]/40 bg-gradient-to-r from-[#181A1F] via-[#1F1A12] to-[#181A1F] p-4 sm:p-5 rounded-sm"
               data-testid="welcome-banner">
            <button onClick={dismissBanner} data-testid="welcome-banner-dismiss"
                    className="absolute top-2 right-2 text-[#6E6B64] hover:text-[#E6E1D6] p-1"
                    aria-label="Dismiss">
              <X size={14} />
            </button>
            <div className="flex items-start gap-3 pr-6">
              <Sparkles size={22} className="text-[#D4AF37] shrink-0 mt-1" />
              <div className="min-w-0">
                <div className="font-heading text-lg sm:text-xl text-[#E6E1D6] leading-snug">
                  Welcome — your free chart & complete Vedic + Homeopathy insights are here.
                </div>
                <div className="mt-1 text-xs sm:text-sm text-[#A19E95] font-light leading-relaxed">
                  Enter your birth details on the left to unlock the full analysis suite — 23 tabs including the new <span className="text-[#D4AF37]">Full Life</span> deep-dive. For a candid personal reading,
                  remedies or a live consultation, book a session with{" "}
                  <span className="text-[#D4AF37] font-medium">Dr. Tejakumar Galla · M.A. Astrology · BHMS</span>.
                </div>
                {!pinOk && (
                  <div className="mt-3 text-[10px] uppercase tracking-widest font-mono text-[#A8896C]">
                    Free chart generation is open below. Enter PIN 6311 on any other tab to unlock the full analysis suite.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8 grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-8">
        <aside className="lg:col-span-3 space-y-3 sm:space-y-4">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-widest text-[#D4AF37] font-mono">{t("Birth Profiles")}</div>
            <Button data-testid={PROFILE.createBtn} size="sm" variant="ghost"
                    onClick={() => setShowForm(true)}
                    className="text-[#A19E95] hover:text-[#D4AF37] hover:bg-transparent h-9 w-9">
              <Plus size={18} />
            </Button>
          </div>
          {/* Horizontal scroll on mobile, vertical stack on desktop */}
          <div className="flex lg:block gap-2 lg:gap-0 space-y-0 lg:space-y-2 overflow-x-auto lg:overflow-visible -mx-4 lg:mx-0 px-4 lg:px-0 pb-2 lg:pb-0 snap-x lg:snap-none">
            {profiles.map(p => (
              <div key={p.profile_id} data-testid={PROFILE.card}
                   className={`shrink-0 lg:shrink border p-3 cursor-pointer transition-colors duration-200 min-w-[220px] lg:min-w-0 snap-start
                              ${active?.profile_id === p.profile_id
                                ? "border-[#D4AF37]/60 bg-[#181A1F]"
                                : "border-[#2A2D35] hover:border-[#D4AF37]/30 bg-[#181A1F]/40"}`}
                   onClick={() => selectProfile(p)}>
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <div className="font-heading text-lg text-[#E6E1D6] truncate">{p.name}</div>
                    <div className="text-[10px] font-mono text-[#6E6B64] mt-1">
                      {p.dob} · {p.tob}
                    </div>
                    <div className="text-[10px] font-mono text-[#6E6B64] truncate max-w-[180px]">
                      {p.place_name}
                    </div>
                  </div>
                  <button data-testid={PROFILE.deleteBtn}
                          onClick={(e) => { e.stopPropagation(); handleDelete(p); }}
                          className="text-[#6E6B64] hover:text-[#C15C3D] transition-colors duration-150 p-1">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </aside>

        <main className="lg:col-span-9 space-y-4 sm:space-y-6 min-w-0">
          {showForm && (
            <ProfileForm onCreated={handleCreated} />
          )}

          {active && chart && (
            <>
              <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-end sm:justify-between gap-3 sm:gap-4">
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-widest text-[#A19E95] font-mono">{t("Native")}</div>
                  <div className="font-heading text-3xl sm:text-4xl text-[#E6E1D6] tracking-tight truncate">{active.name}</div>
                  <div className="font-mono text-xs text-[#6E6B64] mt-1 truncate">
                    {active.dob} · {active.tob} · {active.place_name} · {active.ayanamsha}
                  </div>
                </div>
                <div className="flex items-end gap-2 flex-wrap">
                  <input type="date" value={pdfDate} onChange={e => setPdfDate(e.target.value)}
                         data-testid="pdf-date-input"
                         className="bg-[#0F1115] border border-[#2A2D35] text-[#E6E1D6] font-mono px-2 py-2 text-sm rounded-sm w-40" />
                  <Button
                    data-testid="masterpiece-screener-btn"
                    onClick={() => setShowScreener(true)}
                    variant="outline"
                    className="rounded-sm border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0F1115] transition-colors duration-200">
                    <LayoutGrid size={16} className="mr-2" /> Screener 205
                  </Button>
                  <label
                    data-testid="upload-photo-btn"
                    className="rounded-sm border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0F1115] transition-colors duration-200 flex items-center px-3 h-10 cursor-pointer text-sm">
                    <Camera size={16} className="mr-2" />
                    Cover Photo
                    <input type="file" accept="image/*" className="hidden"
                             onChange={(e) => e.target.files?.[0] && uploadPhoto(e.target.files[0])} />
                  </label>
                  {masterJob.status === "running" ? (
                    <div data-testid="master-progress"
                          className="flex items-center gap-2 border border-emerald-500/60 bg-emerald-500/10 rounded-sm px-3 h-10 text-emerald-300 text-sm min-w-[280px]"
                          title={`current: ${masterJob.current || "…"}`}>
                      <Loader2 size={14} className="animate-spin" />
                      <span className="font-mono text-xs">
                        MASTERPIECE {masterJob.done}/{masterJob.total || 268}
                      </span>
                      <div className="flex-1 h-1.5 bg-[#0F1115] rounded-full overflow-hidden">
                        <div className="h-full bg-emerald-400 transition-all"
                              style={{ width: `${Math.round(100 * (masterJob.done || 0) / (masterJob.total || 268))}%` }} />
                      </div>
                      <button
                        data-testid="master-cancel-btn"
                        onClick={cancelMasterpiece}
                        title="Cancel Masterpiece"
                        className="p-1 text-emerald-400/70 hover:text-red-400 hover:bg-red-500/10 rounded-sm">
                        <X size={14} />
                      </button>
                    </div>
                  ) : masterJob.status === "paused" ? (
                    <div data-testid="master-paused"
                          className="flex items-center gap-2 border border-amber-500/60 bg-amber-500/10 rounded-sm px-3 h-10 text-amber-300 text-sm min-w-[300px]"
                          title={masterJob.error || ""}>
                      <span className="font-mono text-xs">
                        MASTERPIECE PAUSED · {masterJob.done}/{masterJob.total || 268}
                      </span>
                      <Button data-testid="master-resume-btn"
                                onClick={resumeMasterpiece} size="sm"
                                className="ml-auto h-7 px-2 rounded-sm bg-amber-500 text-[#0F1115] hover:bg-amber-400 text-xs">
                        <Zap size={11} className="mr-1" /> Resume
                      </Button>
                      <button data-testid="master-cancel-btn"
                                onClick={cancelMasterpiece}
                                className="p-1 text-amber-400 hover:text-red-400 hover:bg-red-500/10 rounded-sm">
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <Button
                      data-testid="master-start-btn"
                      onClick={startMasterpiece}
                      className="rounded-sm bg-gradient-to-r from-emerald-600 to-emerald-500 text-[#0F1115] hover:from-emerald-500 hover:to-emerald-400 font-semibold shadow-md flex flex-col items-center gap-0 py-1 h-auto">
                      <span className="flex items-center">
                        <Sparkles size={16} className="mr-2" />
                        {masterJob.status === "done"
                          ? "Regenerate Full Masterpiece"
                          : "Generate Full Masterpiece"}
                      </span>
                      <span className="text-[9px] font-mono opacity-90 tracking-widest uppercase">
                        ≈ ₹499 · 268 readings
                      </span>
                    </Button>
                  )}

                  {/* ---------- Strategic Life Map (30-page flagship) ---------- */}
                  {lifeMapJob.status === "running" ? (
                    <div data-testid="lifemap-progress"
                          className="flex items-center gap-2 border border-[#D4AF37]/70 bg-gradient-to-r from-[#3D2E0A]/60 to-[#241A05]/60 rounded-sm px-3 h-10 text-[#F0D67E] text-sm min-w-[320px]"
                          title={`current: ${lifeMapJob.current || "…"}`}>
                      <Map size={14} className="text-[#D4AF37]" />
                      <Loader2 size={12} className="animate-spin" />
                      <span className="font-mono text-xs whitespace-nowrap">
                        LIFE MAP {lifeMapJob.done}/{lifeMapJob.total || 10}
                      </span>
                      <div className="flex-1 h-1.5 bg-[#0F1115] rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-[#D4AF37] to-[#E5C158] transition-all"
                              style={{ width: `${Math.round(100 * (lifeMapJob.done || 0) / (lifeMapJob.total || 10))}%` }} />
                      </div>
                      <button data-testid="lifemap-cancel-btn"
                                onClick={cancelLifeMap} title="Cancel Life Map"
                                className="p-1 text-[#F0D67E]/70 hover:text-red-400 hover:bg-red-500/10 rounded-sm">
                        <X size={14} />
                      </button>
                    </div>
                  ) : lifeMapJob.cached ? (
                    <div className="flex items-center gap-1">
                      <Button
                        data-testid="lifemap-download-btn"
                        onClick={downloadLifeMap}
                        className="rounded-sm bg-gradient-to-r from-[#8B5A00] via-[#D4AF37] to-[#E5C158] text-[#0F1115] hover:brightness-110 font-semibold shadow-md">
                        <Map size={16} className="mr-2" />
                        Strategic Life Map
                      </Button>
                      <button
                        data-testid="lifemap-regen-btn"
                        onClick={startLifeMap}
                        title="Regenerate from scratch"
                        className="rounded-sm border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0F1115] transition-colors h-10 w-10 flex items-center justify-center">
                        <Sparkles size={14} />
                      </button>
                    </div>
                  ) : (
                    <Button
                      data-testid="lifemap-start-btn"
                      onClick={startLifeMap}
                      className="rounded-sm bg-gradient-to-r from-[#8B5A00] via-[#D4AF37] to-[#E5C158] text-[#0F1115] hover:brightness-110 font-semibold shadow-md">
                      <Map size={16} className="mr-2" />
                      Generate Strategic Life Map
                    </Button>
                  )}
                  {/* ---------- /Strategic Life Map ---------- */}
                </div>
              </div>

              <Tabs value={tabValue} onValueChange={handleTabChange} className="w-full" key={`tabs-${lang}`}>
                <TabsList className="bg-transparent border-b border-[#B08A3E]/30 w-full rounded-none h-auto p-0 grid grid-cols-3 sm:grid-cols-6 lg:grid-cols-9 gap-1 justify-start">
                  {[
                    ["charts", t("Charts"), DASH.tabCharts, "#B0413E", 0],
                    ["chartviews", t("Chart Views"), "tab-chartviews", "#C15C3D", 0],
                    ["strengths", t("Strengths"), DASH.tabStrengths, "#B08A3E", 12],
                    ["attributes", t("Attributes"), "tab-attributes", "#8B7A3E", 12],
                    ["yogas", t("Yogas · 300"), "tab-yogas", "#7B906F", 18],
                    ["yoga_atlas", t("Yoga Atlas 300"), "tab-yoga-atlas", "#5C8A3F", 30],
                    ["clients", t("Clients"), "tab-clients", "#4A6BA6", 0],
                    ["dasha", t("Dasha"), DASH.tabDasha, "#5C8A6F", 12],
                    ["day", t("Day"), DASH.tabDay, "#4A8FA1", 12],
                    ["panchang", t("Panchang"), "tab-panchang", "#3F7999", 12],
                    ["nakshatra", t("Nakshatra"), "tab-navatharaka", "#5560A5", 15],
                    ["homeo", t("Homeopathy"), DASH.tabHomeopathy, "#7A5AA6", 25],
                    ["miasm", t("Miasm"), "tab-miasm", "#9757A8", 20],
                    ["kentian", t("Kentian"), "tab-kentian", "#B0559A", 20],
                    ["kingdoms", t("Kingdoms"), "tab-kingdoms", "#B85374", 18],
                    ["atma_darshan", t("Atma Darshan"), "tab-atma-darshan", "#A85B2E", 20],
                    ["saravali", t("Saravali"), "tab-saravali", "#98692B", 15],
                    ["horasara", t("Horasara"), "tab-horasara", "#8A7228", 15],
                    ["phaladeepika", t("Phaladeepika"), "tab-phaladeepika", "#7C7A2C", 15],
                    ["brihat_jataka", t("Brihat Jataka"), "tab-brihat-jataka", "#6C8232", 18],
                    ["theosophy", t("Theosophy"), "tab-theosophy", "#5C8A3F", 20],
                    ["esoteric", t("Esoteric"), "tab-esoteric", "#498A55", 20],
                    ["nakshatra_wisdom", t("Nakshatra Wisdom"), "tab-nakshatra-wisdom", "#3C7A99", 25],
                    ["lifespan", t("Life Span"), "tab-lifespan", "#5B5AA6", 22],
                    ["redalerts", t("Red Alerts"), "tab-redalerts", "#B0413E", 20],
                    ["numerology", t("Numerology"), "tab-numerology", "#8B6E3E", 15],
                    ["vimcsv", t("Vim CSV"), "tab-vimcsv", "#7A5AA6", 12],
                    ["turning", t("Turning Points"), "tab-turning", "#B85374", 25],
                    ["muhurta", t("Muhurta"), "tab-muhurta", "#4A8FA1", 18],
                    ["dashatransit", t("Dasha-Transit"), "tab-dashatransit", "#5560A5", 20],
                    ["remedies", t("Remedies"), "tab-remedies", "#7B906F", 15],
                    ["goodalerts", t("Good Alerts"), "tab-goodalerts", "#5C8A3F", 18],
                    ["fulllife", t("Full Life 500p"), "tab-fulllife", "#B08A3E", 60],
                  ].map(([v,label,tid,color,cost]) => {
                    const isClients = v === "clients";
                    return (
                    <TabsTrigger key={v} value={v} data-testid={tid}
                      style={{ "--tab-color": color,
                                 background: isClients ? `${color}30` : `${color}12`,
                                 borderColor: isClients ? color : `${color}44`,
                                 borderWidth: isClients ? 2 : 1,
                                 boxShadow: isClients ? `0 0 0 2px ${color}55` : "none" }}
                      className="rounded-md border px-2 py-2 text-[#3A2A0F] flex flex-col items-start gap-0.5 h-auto text-left
                                 hover:bg-[color:var(--tab-color)]/25
                                 data-[state=active]:bg-[color:var(--tab-color)] data-[state=active]:text-white
                                 data-[state=active]:border-[color:var(--tab-color)] data-[state=active]:shadow-md
                                 transition-colors duration-200 font-heading text-sm">
                      <span className="truncate w-full leading-tight">{label}</span>
                      <span className="text-[9px] font-mono opacity-80 tracking-wider">
                        {cost === 0 ? "FREE" : `≈ ₹${cost}`}
                      </span>
                    </TabsTrigger>);
                  })}
                </TabsList>

                {/* Universal Download-PDF strip — targets the ACTIVE tab. */}
                <div className="flex items-center justify-between gap-2 py-3 mt-3 border-b border-[#B08A3E]/25">
                  <div className="text-[10px] uppercase tracking-[0.35em] font-mono text-[#7A6642]">
                    Active tab · <span className="text-[#B08A3E]">{tabValue}</span>
                  </div>
                  <Button
                    data-testid={`tab-pdf-${tabValue}`}
                    onClick={() => {
                      const lang = localStorage.getItem("jyotish-lang") || "en";
                      const url = `${process.env.REACT_APP_BACKEND_URL}/api/profiles/${active.profile_id}/topic-pdf?topic=${encodeURIComponent(tabValue)}&lang=${lang}`;
                      window.open(url, "_blank");
                    }}
                    className="rounded-sm bg-gradient-to-r from-[#B08A3E] to-[#D4AF37] text-[#0F1115] hover:brightness-110 font-semibold shadow-sm h-8 px-3 text-xs">
                    <Download size={12} className="mr-1.5" />
                    Download this tab as PDF
                  </Button>
                </div>
                <TabsContent value="charts" className="pt-6 space-y-6">
                  <AllVargasView chart={chart} />
                  <ChartsView chart={chart} />
                </TabsContent>
                <TabsContent value="chartviews" className="pt-6"><ChartViewsPanel profileId={active.profile_id} /></TabsContent>
                <TabsContent value="strengths" className="pt-6"><StrengthsView strengths={strengths} /></TabsContent>
                <TabsContent value="attributes" className="pt-6"><AttributesPanel profileId={active.profile_id} /></TabsContent>
                <TabsContent value="yogas" className="pt-6"><YogasPanel profileId={active.profile_id} /></TabsContent>
                <TabsContent value="yoga_atlas" className="pt-6"><YogaAtlasPanel profileId={active.profile_id} /></TabsContent>
                <TabsContent value="clients" className="pt-6">
                  <ClientsPanel onOpenProfile={(p) => { selectProfile(p); setTabValue("charts"); }} />
                </TabsContent>
                <TabsContent value="dasha" className="pt-6"><DashaTimeline dasha={dasha} /></TabsContent>
                <TabsContent value="day" className="pt-6 space-y-6">
                  <MonthlyReportPanel profileId={active.profile_id} profile={active} />
                  <DayPrediction profileId={active.profile_id} />
                </TabsContent>
                <TabsContent value="panchang" className="pt-6"><PanchangPanel profile={active} /></TabsContent>
                <TabsContent value="nakshatra" className="pt-6"><NavatharakaPanel profileId={active.profile_id} /></TabsContent>
                <TabsContent value="nakshatra_wisdom" className="pt-6"><NakshatraWisdomPanel profileId={active.profile_id} profile={active} /></TabsContent>
                <TabsContent value="atma_darshan" className="pt-6"><AtmaDarshanPanel profileId={active.profile_id} /></TabsContent>
                <TabsContent value="homeo" className="pt-6"><HomeopathyPanel profileId={active.profile_id} /></TabsContent>
                <TabsContent value="miasm" className="pt-6"><MiasmPanel profileId={active.profile_id} /></TabsContent>
                <TabsContent value="kentian" className="pt-6"><KentianView profileId={active.profile_id} /></TabsContent>
                <TabsContent value="kingdoms" className="pt-6"><KingdomsPanel profileId={active.profile_id} /></TabsContent>
                <TabsContent value="esoteric" className="pt-6">
                  <ScripturePanel profileId={active.profile_id} tag="esoteric"
                    title="Esoteric Astrology (A. Bailey)"
                    description="Passages from your uploaded Esoteric Astrology PDF matched to this profile's planet-sign positions. No AI." />
                </TabsContent>
                <TabsContent value="saravali" className="pt-6">
                  <ScripturePanel profileId={active.profile_id} tag="saravali"
                    title="Sāravalī (Kalyana Varma)"
                    description="Passages from Kalyana Varma's Sāravalī matched to this profile's chart. No AI." />
                </TabsContent>
                <TabsContent value="horasara" className="pt-6">
                  <ScripturePanel profileId={active.profile_id} tag="horasara"
                    title="Horāsāra (Prithuyasas)"
                    description="Passages from Prithuyasas' Horāsāra matched to this profile's chart. No AI." />
                </TabsContent>
                <TabsContent value="phaladeepika" className="pt-6">
                  <ScripturePanel profileId={active.profile_id} tag="phaladeepika"
                    title="Phaladeepika (Mantreswara)"
                    description="Passages from Mantreswara's Phaladeepika matched to this profile's chart. No AI." />
                </TabsContent>
                <TabsContent value="brihat_jataka" className="pt-6">
                  <ScripturePanel profileId={active.profile_id} tag="brihat_jataka"
                    title="Brihat Jataka (Varāha Mihira)"
                    description="Passages from Varāha Mihira's Brihat Jataka matched to this profile's chart. No AI." />
                </TabsContent>
                {[
                  ["theosophy","The Theosophical Engine","Chart-grounded esoteric predictions from Alice Bailey (24 vols), H.P. Blavatsky, Rudhyar, Powell & Leadbeater — every prediction cites its source. Powered by GPT-4o-mini."],
                  ["esoteric","Esoteric Astrology (fresh chart analysis)","A pure Alice Bailey + Alan Leo synthesis of THIS chart — Seven Rays, exoteric/esoteric/hierarchical rulerships, the soul's evolutionary intent. Authored entirely by GPT-4o-mini from classical doctrine — no PDF passages."],
                ].map(([tag,title,desc]) => (
                  <TabsContent key={tag} value={tag} className="pt-6">
                    <ScripturePanel profileId={active.profile_id} tag={tag} title={title} description={desc} />
                  </TabsContent>
                ))}
                <TabsContent value="lifespan" className="pt-6"><LifeSpanPanel profileId={active.profile_id} /></TabsContent>
                <TabsContent value="redalerts" className="pt-6"><RedAlertsPanel profileId={active.profile_id} /></TabsContent>
                <TabsContent value="numerology" className="pt-6"><NumerologyPanel /></TabsContent>
                <TabsContent value="vimcsv" className="pt-6"><VimshottariCsvPanel profile={active} /></TabsContent>
                <TabsContent value="turning" className="pt-6">
                  <DateRangeScanner profileId={active.profile_id} kind="turning" />
                  <AstroTopicPanel profileId={active.profile_id} topic="turning-points"
                    title="Major Turning Points"
                    description="Dasha-driven windows for marriage, career, business, investments, property, childbirth, travel, education, retirement."
                    subtopics={TURNING_POINT_SUBS} />
                </TabsContent>
                <TabsContent value="muhurta" className="pt-6">
                  <AstroTopicPanel profileId={active.profile_id} topic="muhurta"
                    title="Muhurta — Electional Timing"
                    description="Auspicious start-timing for marriage, house-warming, business, vehicle, surgery, travel, contracts, investments."
                    subtopics={MUHURTA_SUBS} />
                </TabsContent>
                <TabsContent value="dashatransit" className="pt-6">
                  <AstroTopicPanel profileId={active.profile_id} topic="dasha-transit"
                    title="Dasha & Transit Analysis"
                    description="Current Maha / Antar / Pratyantar effects, Sade-Sati, Jupiter, Rahu-Ketu, and annual/monthly/yearly transit forecasts."
                    subtopics={DASHA_TRANSIT_SUBS} />
                </TabsContent>
                <TabsContent value="remedies" className="pt-6">
                  <AstroTopicPanel profileId={active.profile_id} topic="remedies"
                    title="Classical Remedies"
                    description="Chart-tailored mantras, donations, fasts, gemstones, yantras, temple visits, homas, rudraksha, daily practices."
                    subtopics={REMEDY_SUBS} />
                </TabsContent>
                <TabsContent value="goodalerts" className="pt-6">
                  <DateRangeScanner profileId={active.profile_id} kind="good" />
                  <AstroTopicPanel profileId={active.profile_id} topic="good-alerts"
                    title="Good Time Alerts"
                    description="Auspicious windows for the next 30–90 days: Sarvartha Siddhi, Guru/Ravi Pushya, Amrita Siddhi and benefic transits."
                    subtopics={GOOD_ALERT_SUBS} />
                </TabsContent>
                <TabsContent value="fulllife" className="pt-6">
                  <FullLifePanel profileId={active.profile_id} />
                </TabsContent>
              </Tabs>
            </>
          )}
        </main>
      </div>
      {/* Global PIN prompt (appears when user clicks a locked tab) */}
      {pinPrompt && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
             onClick={() => setPinPrompt(false)} data-testid="pin-prompt-overlay">
          <form onClick={(e) => e.stopPropagation()}
                onSubmit={(e) => {
                  e.preventDefault();
                  if (pinInput.trim() === "111222333444555") {
                    unlockPin();
                    setPinPrompt(false);
                    if (pendingTab) { setTabValue(pendingTab); setPendingTab(null); }
                    setPinInput(""); setPinErr("");
                  } else {
                    setPinErr("Incorrect PIN"); setPinInput("");
                  }
                }}
                className="w-full max-w-sm bg-[#0F1115] border border-[#D4AF37]/40 rounded-sm p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="border border-[#D4AF37]/40 p-2 text-[#D4AF37]"><Lock size={16} /></div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] font-mono">Locked</div>
                <div className="font-heading text-lg text-[#E6E1D6]">Enter PIN to unlock</div>
              </div>
            </div>
            <div className="text-xs text-[#A19E95] font-mono">
              Chart generation and consultations are open to all. Enter the 4-digit PIN to unlock the rest of the analysis suite for this session.
            </div>
            <input type="password" inputMode="numeric" autoFocus maxLength={8}
                   value={pinInput}
                   onChange={(e) => { setPinInput(e.target.value.replace(/[^\d]/g,"")); setPinErr(""); }}
                   data-testid="pin-prompt-input" placeholder="••••"
                   className="w-full bg-[#181A1F] border border-[#2A2D35] focus:border-[#D4AF37] outline-none
                              text-[#E6E1D6] font-mono text-center text-2xl tracking-[0.6em] py-3 rounded-sm" />
            {pinErr && <div className="text-red-400 text-xs font-mono" data-testid="pin-prompt-error">{pinErr}</div>}
            <div className="flex gap-2">
              <button type="submit" data-testid="pin-prompt-submit"
                      className="flex-1 rounded-sm bg-[#D4AF37] text-[#0F1115] hover:bg-[#E5C158] font-heading py-2">
                Unlock
              </button>
              <button type="button" onClick={() => { setPinPrompt(false); setPendingTab(null); setPinInput(""); setPinErr(""); }}
                      className="px-4 py-2 rounded-sm border border-[#2A2D35] text-[#A19E95] font-mono text-xs uppercase tracking-widest">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
      <ActiveTabExporter />
      {showScreener && active?.profile_id && (
        <MasterpieceScreener
          profileId={active.profile_id}
          lang={localStorage.getItem("jyotish-lang") || "en"}
          onClose={() => setShowScreener(false)}
        />
      )}
    </div>
  );
}
